import React from "react";
import { IconButton } from "@mui/material";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import EditIcon from "@mui/icons-material/Edit";

export function formatGridValue(value, { objectSeparator = ",\n", keyValueSeparator = ": ", depth = 0 } = {}) {
  if (value == null || value === "") return "";

  if (Array.isArray(value)) {
    return value
      .map(v => (typeof v === "object" && v !== null) ? formatGridValue(v, { objectSeparator, keyValueSeparator, depth: depth + 1 }) : String(v))
      .join(",\n");
  }

  if (typeof value === "object") {
    const indent = "  ".repeat(depth + 1);
    return Object.entries(value)
      .map(([k, v]) => {
        if (v !== null && typeof v === "object") {
          // Nested object/array: put key on its own line, indent its contents
          const nested = formatGridValue(v, { objectSeparator, keyValueSeparator, depth: depth + 1 });
          return `${indent}${k}${keyValueSeparator}\n${nested.split("\n").map(l => "  " + l).join("\n")}`;
        }
        return `${indent}${k}${keyValueSeparator}${v}`;
      })
      .join(objectSeparator);
  }

  return String(value);
}

export const disableLink = (record) => {
  return record.request_status === "In-Progress" ? null : record.signed_url;
};

export const activityLogsGridColumns = {
  created: "Created",
  customer_id: "Customer ID",
  action: "Action",
  additional_data: "Additional Data",
  details: "Details",
  actor: "Actor",
  resource_type: "Resource Type",
};

export const activityLogsOptionalColumns = {
  unique_id: "Log ID",
  resource_id: "Resource ID",
};

export const pdfGridColumns = {
  request_timestamp: "Timestamp",
  campaign: "Campaign",
  current_contacts: "Contacts",
  custom_template_name: "Saved Template",
  delivery_method: "Delivery Method",
  request_status: "Status",
  status_updated: "Status Updated",
  conditional_url: "File",
  credit_cost: 'Credit Cost',
  credit_charged_base: 'Base Credit',
  credit_charged_extra: 'Extra Credit'
};

export const adminPdfGridColumns = {
  edit: "Actions",
  request_timestamp: "Timestamp",
  customer_id: "Customer ID",
  campaign: "Campaign",
  current_contacts: "Contacts",
  custom_template_name: "Saved Template",
  delivery_method: "Delivery Method",
  request_status: "Status",
  status_updated: "Status Updated",
  conditional_url: "File",
  credit_cost: 'Credit Cost',
  credit_charged_base: 'Base Credit',
  credit_charged_extra: 'Extra Credit'
};

export const adminPdfOptionalColumns = {
  unique_id: "Unique ID"
}

export const campaignGridColumns = {
  campaign_name: "Campaign",
  total: "#Contacts",
};

export const respondedGridColumns = {
  greeting_name: "Full Name",
  "ma-addr_line1": "Street",
};

export const contactVisitsGridColumns = {
  visited: "visited",
  trackable: "qrcode-id",
  campaign: "campaign",
};

export const existingUsersGridColumns = {
  created: "Created",
  organization: "Organization",
  user_name: "User Name",
  email: "Email",
  phone: "Phone",
  vendors: "Partners",
  confirmation_status: 'Confirmation Status'
};

export const usersGridOptionalColumns = {
  customer_id: "Customer ID",
  tpx_id: "Tracking Pixel",
  user_id: "User ID",
  cognito_pool_id: "Cognito Pool ID",
  role: "Role"
};

export const savedTemplatesGridColumns = {
  view: 'View',
  choice: 'Template',
  template_name: 'Saved Template',
  created: 'Created',
  owner: "Owner",
  type: "Type"
}
export const existingPlansGridColumns = {
  name: 'Plan Name',
  description: 'Description',
  monthly_credits: 'Monthly Credits',
  monthly_price: 'Monthly Price',
  annual_price: 'Annual Price',
  credit_overage_price: 'Overage Price',
  credit_costs: 'Credit Costs',
  quotas: 'Quotas',
}

export const plansGridOptionalColumns = {
  plan_id: 'Plan ID',
  created: 'Created',
}

export const existingSubscriptionsGridColumns = {
  user_name: 'User Name',
  email: "Email",
  plan_name: "Plan",
  billing_cycle: 'Billing Cycle',
  status: 'Status',
  payment_status: 'Payment Status',
  plan_start_date: 'Plan Start Date',
  plan_end_date: 'Plan End Date',
  created: 'Created',
  subscription_id: 'Subscription ID',
}

export const subscriptionsGridOptionalColumns = {
  updated: 'Updated',
  start_date: "Subscription Start Date",
  customer_id: 'Customer ID',
}

export const existingQuotaExtensionsGridColumns = {
  credit_limit: "Credit Limit",
  credit_used: "Credit Used",
  quota_limit: "Quota Limit",
  quota_used: "Quota Used",
  start_date: "Start Date",
  end_date: "End Date",
  usage_metric_id: "Metric ID",
}

export const existingExtraCreditsGridColumns = {
  extra_credit_limit: "Credit Limit",
  extra_credit_used: "Credit Used",
  status: "Status",
  price: "Price",
  payment_status: "Payment Status",
  created: "Created",
  updated: "Updated",
  extra_credit_id: "Extra Credit ID"
}

export const quotaExtensionsOptionalColumns = {
  usage_metric_id: "Usage Metric ID",
  created_timestamp: "Created"
}

export const existingActivitiesGridColumns = {
  action: "Action",
  additional_data: "Additional Data",
  created: "Created",
  activity_log_id: "Activity ID"
}
export const existingAdminActivitiesGridColumns = {
  timestamp: "Timestamp",
  actor: "Actor",
  resource_type: "Resource Type",
  resource_id: "Resource ID",
  action: "Action"
}

export const existingPaymentsGridColumns = {
  amount: "Amount",
  purpose: "Purpose",
  description: "Description",
  created: "Created",
  payment_id: "Payment ID"
}
const DATE_FIELDS = new Set([
  'start_date',
  'end_date',
  'created_timestamp',
  'updated_timestamp',
  'timestamp',
  'created',
  'updated',
  'request_timestamp',
  'status_updated',
  'visited',
  'plan_end_date',
  'plan_start_date',
]);

export const usageMetricGridColumns = {
  plan_name: "Plan Name",
  billing_cycle: "Billing Cycle",
  credit_limit: "Credit Limit",
  credit_used: "Credit Used",
  quota_limit: "Quota Limit",
  quota_used: "Quota Used",
  start_date: "Start Date",
  end_date: "End Date",
  usage_metric_id: "Metric ID",
}

export const usageMetricGridOptionalColumns = {
  created: "Created",
  updated: "Updated"
}

export const generateGridColumns = (
  mappingConfig,
  alwaysVisibleColumn,
  onView,
  onEdit,
  optionalColumns = {}
) => {
  const mainColumns = Object.keys(mappingConfig).map((key) => ({
    field: key,
    headerName: mappingConfig[key],
    flex: key === 'view' || key === 'edit' ? 0.3 : 1,
    minWidth: key === 'view' || key === 'edit' ? 70 : 150,
    hide: false,
    hideable: key !== alwaysVisibleColumn && key !== 'view' && key !== 'edit',
    sortable: key !== 'view' && key !== 'edit',
    filterable: key !== 'view' && key !== 'edit',
    renderCell: (() => {

      if (key === "quotas" || key === "used_quota" || key === "credit_costs") {
        return (params) => <div style={{ whiteSpace: "pre-line" }}>{params.value}</div>;
      }

      if (key === "edit" && onEdit) {
        return (params) => (
          <IconButton
            size="small"
            color="primary"
            onClick={(e) => {
              onEdit(params.row);
            }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        );
      }
      if (key === "view" && onView) {
        return (params) => (
          <IconButton
            size="small"
            color="primary"
            onClick={(e) => {
              onView(params.row);
            }}
          >
            <RemoveRedEyeIcon fontSize="small" />
          </IconButton>
        );
      }
      if (key === "conditional_url") {
        return (params) => {
          if (!params.value) return <span>None</span>;
          const urls = typeof params.value === "string"
            ? params.value.split(",").map(u => u.trim()).filter(u => u)
            : [params.value];

          if (urls.length === 0) return <span>None</span>;

          const collateralType = params.row.collateral_type || "None";
          const types = collateralType.split(",").map(t => t.trim());

          if (urls.length === 1) {
            return (
              <a
                href={urls[0]}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => {
                  e.stopPropagation();
                }}
                style={{ textDecoration: "none", color: "#1976d2", fontWeight: 500 }}
              >
                {collateralType !== "None" ? collateralType : "None"}
              </a>
            );
          }

          return (
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              {urls.map((url, index) => (
                <React.Fragment key={index}>
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    style={{ textDecoration: "none", color: "#1976d2", fontWeight: 500 }}
                  >
                    {types[index] || "None"}
                  </a>
                  {index < urls.length - 1 && <span style={{ color: '#ccc' }}>|</span>}
                </React.Fragment>
              ))}
            </div>
          );
        };
      }
      if (key === "details") {
        return (params) => {
          if (typeof params.value !== "string") return params.value;
          const parts = params.value.split(" -> ");
          if (parts.length === 2) {
            return (
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0, width: 'max-content' }}>
                {parts[0]}
                <span style={{ color: '#000', fontWeight: 'bold', fontSize: '1.2em', lineHeight: 1 }}>
                  →
                </span>
                {parts[1]}
              </span>
            );
          }
          return params.value;
        };
      }

      return undefined;
    })(),
  }));

  const optionalCols = Object.keys(optionalColumns).map((key) => ({
    field: key,
    headerName: optionalColumns[key],
    flex: 1,
    minWidth: 150,
    hide: true,
    hideable: true,
  }));
  return [...mainColumns, ...optionalCols];
};

export const pdfRequestRowMapping = {
  request_timestamp: "request_timestamp",
  campaign: "campaign",
  current_contacts: "current_contacts",
  custom_template_name: "custom_template_name",
  delivery_method: "delivery_method",
  request_status: "request_status",
  conditional_url: "conditional_url",
  unique_id: "unique_id",
  status_updated: "status_updated",
  credit_cost: "credit_cost",
  credit_charged_base: "credit_charged_base",
  credit_charged_extra: "credit_charged_extra"
};

export const adminPdfRequestRowMapping = {
  edit: "edit",
  request_timestamp: "request_timestamp",
  customer_id: "customer_id",
  campaign: "campaign",
  current_contacts: "current_contacts",
  custom_template_name: "custom_template_name",
  delivery_method: "delivery_method",
  request_status: "request_status",
  conditional_url: "signed_url",
  unique_id: "unique_id",
  status_updated: "status_updated",
  credit_cost: "credit_cost",
  credit_charged_base: "credit_charged_base",
  credit_charged_extra: "credit_charged_extra"
};


export const activityLogsRowMapping = {
  unique_id: "unique_id",
  resource_type: "resource_type",
  resource_id: "resource_id",
  customer_id: "customer_id",
  additional_data: "additional_data",
  action: "action",
  details: "details",
  actor: "actor",
  created: "created",
  unique_id: "unique_id",
}

export const campaignRowMapping = {
  campaign_name: "campaign_name",
  total: "total",
};
export const respondedRowMapping = {
  greeting_name: "greeting_name",
  "ma-addr_line1": "ma-addr_line1",
};
export const contactVisitsRowMapping = {
  visited: "visited",
  trackable: "trackable",
  campaign: "campaign",
};
export const existingUsersRowMapping = {
  created: "created",
  organization: "organization",
  user_name: "user_name",
  email: "email",
  phone: "phone",
  vendors: "vendors",
  confirmation_status: "confirmation_status",

  customer_id: "customer_id",
  tpx_id: "tpx_id",
  user_id: "user_id",
  cognito_pool_id: "cognito_pool_id",
  role: "role",
};

export const savedTemplatesRowMapping = {
  choice: 'choice',
  template_name: 'template_name',
  created: 'created',
  owner: 'owner',
  type: 'type'
}

export const generateGridRows = (data, mappingConfig, startIndex = 1) => {
  return data?.map((record, index) => {
    const row = { id: startIndex + index };
    Object.keys(mappingConfig).forEach((key) => {
      const sourceKey = mappingConfig[key];
      const value = record[sourceKey];
      const formattedValue = DATE_FIELDS.has(sourceKey)
        ? formatUtcToLocal(value)
        : formatGridValue(value);
      row[key] = formattedValue || "-";
    });
    if (record.collateral_type) {
      row.collateral_type = record.collateral_type;
    }
    return row;
  })
    || [];
};

export const extraCreditsDetailsMapping = {
  extra_credit_limit: "Credit Limit",
  extra_credit_used: "Credit Used",
  status: "Status",
  price: "Price",
  payment_status: "Payment Status",
  created: "Created",
  updated: "Updated",
  extra_credit_id: "Extra Credit ID",
};

export const campaignMapping = {
  campaign_name: "Campaign",
  total: "Contacts",
  created: "Created",
  destination_url: "Destination",
};
export const respondedMapping = {
  created: "created",
  trackable_url: "trackable_url",
  trackable: "qrcode-id",
  envelope_name: "envelope_name",
  greeting_name: "greeting_name",
  "ma-addr_line1": "ma-addr_line1",
  "ma-city": "ma-city",
  "ma-state": "ma-state",
  "ma-zip": "ma-zip",
  visits: "visits",
};

export const contactVisitsMapping = {
  visited: "visited",
  trackable: "qrcode-id",
  country: "country",
  user_agent: "user-agent",
  city: "city",
  region: "region",
};

export const activityLogsMapping = {
  created: "Created",
  customer_id: "Customer ID",
  action: "Action",
  additional_data: "Additional Data",
  details: "Details",
  actor: "Actor",
  resource_type: "Resource Type",
  resource_id: "Resource ID",
  unique_id: "Log ID",
}

export const generateFormData = (selectedRow, mappingConfig) => {
  return Object.keys(mappingConfig).map((key) => ({
    label: mappingConfig[key],
    value: DATE_FIELDS.has(key) ? formatUtcToLocal(selectedRow[key]) : selectedRow[key],
  }));
};

export const existingPlansRowMapping = {
  name: 'name',
  description: 'description',
  monthly_credits: 'monthly_credits',
  monthly_price: 'monthly_price',
  annual_price: 'annual_price',
  credit_overage_price: 'credit_overage_price',
  credit_costs: 'credit_costs',
  quotas: 'quotas',
  plan_id: 'plan_id',
  created: 'created',
}

export const existingSubscriptionsRowMapping = {
  subscription_id: 'subscription_id',
  user_name: 'user_name',
  email: 'email',
  plan_name: 'plan_name',
  status: 'status',
  customer_id: 'customer_id',
  subscription_plan_id: 'subscription_plan_id',
  plan_start_date: 'plan_start_date',
  plan_end_date: 'plan_end_date',
  billing_cycle: 'billing_cycle',
  payment_status: 'payment_status',
  created: 'created',
  updated: 'updated',
  start_date: "start_date"
}

export const existingQuotaExtensionsRowMapping = {
  usage_metric_id: "usage_metric_id",
  start_date: "start_date",
  end_date: "end_date",
  quota_limit: "quota_limit",
  quota_used: "quota_used",
  credit_limit: "credit_limit",
  credit_used: "credit_used"
}

export const existingExtraCreditsRowMapping = {
  extra_credit_id: "extra_credit_id",
  extra_credit_limit: "extra_credit_limit",
  extra_credit_used: "extra_credit_used",
  price: "price",
  status: "status",
  created: "created",
  updated: "updated",
  payment_status: "payment_status"
}

export const existingActivitiesRowMapping = {
  created: 'created',
  action: 'action',
  additional_data: 'additional_data',
  activity_log_id: 'activity_log_id'
}

export const usageMetricsRowMapping = {
  quota_id: "quota_id",
  usage_metric_id: "usage_metric_id",
  start_date: "start_date",
  end_date: "end_date",
  created: "created",
  updated: "updated",
  quota_limit: "quota_limit",
  quota_used: "quota_used",
  credit_limit: "credit_limit",
  credit_used: "credit_used",
  plan_name: "plan_name",
  billing_cycle: "billing_cycle",
}

export const paymentsRowMapping = {
  customer_id: "customer_id",
  created: "created",
  description: "description",
  amount: "amount",
  purpose: "purpose",
  payment_id: "payment_id"
}

export const activityMapping = {
  action: 'Action',
  additional_data: 'Additional Data',
  created: 'Created',
  activity_log_id: "Activity ID",
}

export const usageMetricMapping = {
  credit_limit: "Credit Limit",
  credit_used: "Credit Used",
  status: "Status",
  price: "Price",
  created: "Created",
  updated: "updated",
  usage_metric_id: "Metric ID",

};

export const paymentsMapping = {
  amount: "Amount",
  purpose: "Purpose",
  description: "Description",
  created: "Created",
  payment_id: "Payment ID"
}

export const userProfileMapping = {
  customer_id: "Customer ID",
  user_name: "Name",
  email: "Email",
  phone: "Mobile Number",
  organization: "Organization",
  created: "Created Date",
};

function toUtcDate(value) {
  if (value instanceof Date) return value;
  if (typeof value === 'number') return new Date(value);
  if (typeof value !== 'string') return null;

  const hasTZ = /[Zz]$|[+-]\d{2}:?\d{2}$/.test(value);
  const isoUtc = hasTZ
    ? value.replace(' ', 'T')
    : `${value.replace(' ', 'T')}Z`;

  const date = new Date(isoUtc);
  return isNaN(date.getTime()) ? null : date;
}

function formatInTimeZone(value, options) {
  const date = toUtcDate(value);
  if (!date) return value;

  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const locale = navigator.language || 'en-US';

  return date.toLocaleString(locale, { timeZone, ...options });
}

export function formatUtcToLocal(utcValue) {
  return formatInTimeZone(utcValue, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
}

export const subscriptionDetailsFieldConfig = [
  { key: "user_name", label: "User Name" },
  { key: "email", label: "Email" },
  { key: "plan_name", label: "Plan" },
  { key: "billing_cycle", label: "Billing Cycle" },
  { key: "status", label: "Status" },
  { key: "payment_status", label: "Payment Status" },
  { key: "plan_start_date", label: "Start Date", formatter: formatUtcToLocal },
  { key: "plan_end_date", label: "End Date", formatter: formatUtcToLocal },
  { key: "created", label: "Created", formatter: formatUtcToLocal },
  { key: "updated", label: "Updated", formatter: formatUtcToLocal },
  { key: "subscription_id", label: "Subscription ID" },
];

export const extraCreditDetailsFieldConfig = [
  { key: "extra_credit_limit", label: "Credit Limit" },
  { key: "extra_credit_used", label: "Credit Used" },
  { key: "status", label: "Status" },
  { key: "price", label: "Price" },
  { key: "payment_status", label: "Payment Status" },
  { key: "extra_credit_id", label: "ID" },
  { key: "created", label: "Created", formatter: formatUtcToLocal },
  { key: "updated", label: "Updated", formatter: formatUtcToLocal },
];

export const quotaDetailsFieldConfig = [
  { key: "credit_limit", label: "Credit Limit" },
  { key: "credit_used", label: "Credit Used" },
  { key: "start_date", label: "Start Date", formatter: formatUtcToLocal },
  { key: "end_date", label: "End Date", formatter: formatUtcToLocal },
  { key: "usage_metric_id", label: "Quota ID" },
];

// Generic mapper: config + data object -> fields array for DetailsPanel
export const generateDetailFields = (config, data) => {
  if (!data) return [];
  return config.map(({ key, label, formatter }) => ({
    label,
    value: formatter ? formatter(data[key]) : data[key],
  }));
};